/**
 * Buy-a-Feature tool runner.
 * Simulates purchasing features with constrained budget.
 */

import { readToolSettings } from "../lib/toolSettings";
import type {
  Item,
  CriteriaSet,
  ToolConfig,
  Lens,
} from "../harmonizer/types";
import type { NativeScoreSet } from "../harmonizer/testHarness";
import type { RawToolSpec } from "../harmonizer/specLoader";
import { loadToolSpecFile, getToolSpecPath } from "./specLoader";

let cachedSpec: RawToolSpec | null = null;

/**
 * Gets the raw tool spec for Buy-a-Feature.
 */
export function getBuyAFeatureSpec(): RawToolSpec {
  if (cachedSpec) {
    return cachedSpec;
  }

  const specPath = getToolSpecPath("buy-a-feature");
  cachedSpec = loadToolSpecFile(specPath);
  return cachedSpec;
}

/**
 * Runs the Buy-a-Feature tool.
 * 
 * Note: This implementation assumes participant purchases are stored in toolConfig.
 * In a real system, purchases would be collected through interactive UI.
 * 
 * @param items - Items (features) to purchase
 * @param _criteriaSet - CriteriaSet (not used)
 * @param toolConfig - Tool configuration (num_participants, budget_percentage, participant_actions)
 * @param lens - Optional lens for filtering
 * @returns Native score set
 */
export async function runBuyAFeatureTool(
  items: Item[],
  _criteriaSet: CriteriaSet | null,
  toolConfig: ToolConfig,
  lens?: Lens | null
): Promise<NativeScoreSet> {
  const toolRunId = `buy-a-feature-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  // Get config values
  const numParticipants = (toolConfig.num_participants as number) || 1;
  const { budgetPercentage } = readToolSettings(toolConfig);
  const participantActions = (toolConfig.participant_actions as Array<{
    participant_id: string;
    purchases: Array<{ feature_id: string; quantity: number }>;
  }>) || [];

  // Filter items if lens is provided
  let filteredItems = items;
  if (lens) {
    const { applyLens } = await import("../arrange/validation");
    filteredItems = applyLens(items, lens);
  }

  // Calculate total budget
  const totalPrice = filteredItems.reduce((sum, item) => {
    const price = item.attributes.price;
    if (price === null || price === undefined) {
      return sum;
    }
    const priceNum = typeof price === "number" ? price : Number(price);
    return sum + (isNaN(priceNum) ? 0 : priceNum);
  }, 0);

  const totalBudget = totalPrice * budgetPercentage;
  const budgetPerParticipant = totalBudget / numParticipants;

  // Track purchases per item
  const purchaseCounts = new Map<string, number>();
  const totalSpentPerItem = new Map<string, number>();

  // Initialize all items to 0
  for (const item of filteredItems) {
    purchaseCounts.set(item.id, 0);
    totalSpentPerItem.set(item.id, 0);
  }

  // Process participant actions
  let totalSpent = 0;
  for (const participantAction of participantActions) {
    for (const purchase of participantAction.purchases) {
      const item = filteredItems.find((i) => i.id === purchase.feature_id);
      if (!item) {
        continue; // Skip invalid feature IDs
      }

      const price = item.attributes.price;
      if (price === null || price === undefined) {
        continue;
      }
      const priceNum = typeof price === "number" ? price : Number(price);
      if (isNaN(priceNum) || priceNum <= 0) {
        continue;
      }

      const quantity = purchase.quantity || 1;
      const cost = priceNum * quantity;

      // Update purchase counts and total spent
      const currentPurchases = purchaseCounts.get(item.id) || 0;
      const currentSpent = totalSpentPerItem.get(item.id) || 0;

      purchaseCounts.set(item.id, currentPurchases + quantity);
      totalSpentPerItem.set(item.id, currentSpent + cost);
      totalSpent += cost;
    }
  }

  // Build items with purchase data
  const itemsWithPurchases: Array<{
    item: Item;
    purchases: number;
    totalSpent: number;
  }> = [];

  for (const item of filteredItems) {
    const purchases = purchaseCounts.get(item.id) || 0;
    const totalSpent = totalSpentPerItem.get(item.id) || 0;

    itemsWithPurchases.push({
      item,
      purchases,
      totalSpent,
    });
  }

  // Sort by purchases (descending), then by total_spent (descending), then by label (ascending)
  itemsWithPurchases.sort((a, b) => {
    if (b.purchases !== a.purchases) {
      return b.purchases - a.purchases;
    }
    if (b.totalSpent !== a.totalSpent) {
      return b.totalSpent - a.totalSpent;
    }
    return a.item.label.localeCompare(b.item.label);
  });

  // Assign ranks and build output
  const scoredItems: Array<{
    item_id: string;
    purchases: number;
    total_spent: number;
    rank: number;
  }> = [];
  let currentRank = 1;

  for (let i = 0; i < itemsWithPurchases.length; i++) {
    const { item, purchases, totalSpent } = itemsWithPurchases[i];

    // If purchases or totalSpent differ from previous, update rank
    if (
      i > 0 &&
      (itemsWithPurchases[i - 1].purchases !== purchases ||
        itemsWithPurchases[i - 1].totalSpent !== totalSpent)
    ) {
      currentRank = i + 1;
    }

    scoredItems.push({
      item_id: item.id,
      purchases: purchases,
      total_spent: totalSpent,
      rank: currentRank,
    });
  }

  return {
    items: scoredItems,
    toolRunId,
    metadata: {
      total_items: filteredItems.length,
      total_budget: totalBudget,
      total_spent: totalSpent,
      num_participants: numParticipants,
      budget_per_participant: budgetPerParticipant,
    },
  };
}


